'use client';

import { c as _c } from "react/compiler-runtime";
import { createBackerRewardScope, type GetBackerRewardsRow, useActiveWallet, useClaimBackerFees, useGetBackerRewardsInfinite, useGetBackerRewardsSummary, useTurnkeySigningDeps } from '@morfi/backend-sdk-v2/client';
import type { JSX } from 'react';
import { isOwnViewer, usePortfolioContext } from '@/components/portfolio/portfolio-context';
import { BackerRewardsShareButton } from '@/components/portfolio/share/backer-rewards-share-button';
import { D6_SCALE } from '@/config/solana';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { BackerRewardsCardView, type BackerRewardsRowView } from './backer-rewards-card-view';
import { BACKER_REWARDS_FIELDS } from './backer-rewards-fields';
const D6: number = D6_SCALE;
const ZERO: bigint = BigInt(0);
const SUMMARY_FIELDS = ['totalEarnedD6', 'claimableNowD6', 'marketsBacked'] as const;
interface BackerRewardsCardProps {
  handle?: string | null;
  displayName?: string | null;
  className?: string | undefined;
}

/**
 * Backer-rewards portfolio rollup card (mockup #06). One row per market the
 * user has backed, plus three summary tiles (Total earned · Claimable now ·
 * Markets backed). Backer rewards are a SEPARATE earnings stream — this card
 * never feeds any PnL total.
 *
 * The tiles render the backend's `/backer-rewards/summary`, aggregated over
 * every market the user ever backed. The list is paginated, so summing loaded
 * rows would undercount (MOR-2225). Rows stay live via NATS; the summary is
 * refetched by the SDK after a confirmed back or claim.
 * Claim is only enabled on the viewer's own portfolio.
 */
export function BackerRewardsCard(t0) {
  const $ = _c(56);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    className,
    handle: t2,
    displayName: t3
  } = t1;
  const handle = t2 === undefined ? null : t2;
  const displayName = t3 === undefined ? null : t3;
  const ctx = usePortfolioContext();
  let t4;
  if ($[2] !== ctx) {
    t4 = isOwnViewer(ctx);
    $[2] = ctx;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const own = t4;
  const t5 = ctx.financialQueryId;
  let t6;
  if ($[4] !== ctx.userId || $[5] !== ctx.walletPubkeys) {
    t6 = createBackerRewardScope(ctx.userId, ctx.walletPubkeys);
    $[4] = ctx.userId;
    $[5] = ctx.walletPubkeys;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== ctx.walletPubkeys) {
    t7 = ctx.walletPubkeys.join(",");
    $[7] = ctx.walletPubkeys;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [...BACKER_REWARDS_FIELDS];
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== ctx.financialQueryId || $[11] !== t6 || $[12] !== t7) {
    t9 = {
      userId: t5,
      rewardScope: t6,
      walletPubkeysCsv: t7,
      fields: t8
    };
    $[10] = ctx.financialQueryId;
    $[11] = t6;
    $[12] = t7;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = ctx.financialQueryId !== "";
  let t11;
  if ($[14] !== t10) {
    t11 = {
      enabled: t10
    };
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const query = useGetBackerRewardsInfinite(t9, t11);
  const rows = useLiveListData(query.queryKey, query.rows, query.entityQueryKey);
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [...SUMMARY_FIELDS];
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== ctx.financialQueryId) {
    t13 = {
      userId: ctx.financialQueryId,
      fields: t12
    };
    $[17] = ctx.financialQueryId;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const t14 = ctx.financialQueryId !== "";
  let t15;
  if ($[19] !== t14) {
    t15 = {
      enabled: t14
    };
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const summary = useGetBackerRewardsSummary(t13, t15);
  const claim = useClaimBackerFees();
  const {
    activeWallet
  } = useActiveWallet();
  const signingDeps = useTurnkeySigningDeps();
  let t16;
  if ($[21] !== activeWallet || $[22] !== claim || $[23] !== own || $[24] !== rows || $[25] !== signingDeps) {
    t16 = function fireClaim(marketPubkey) {
      if (!own || activeWallet === null || activeWallet === undefined) {
        return;
      }
      const target = rows.find(r => r.marketPubkey === marketPubkey);
      if (target === undefined || !target.canClaim) {
        return;
      }
      claim.mutate({
        marketAddress: marketPubkey,
        burnAmountD6: target.userShareD6,
        activeWallet: {
          id: activeWallet.id,
          address: activeWallet.address,
          type: activeWallet.type
        },
        signingDeps
      });
    };
    $[21] = activeWallet;
    $[22] = claim;
    $[23] = own;
    $[24] = rows;
    $[25] = signingDeps;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const fireClaim = t16;
  const claimingMarket = claim.isPending ? claim.variables?.marketAddress ?? null : null;
  const totals = summary.data;
  if (query.isPending || totals === null || totals === undefined) {
    return null;
  }
  if (totals.marketsBacked === 0) {
    return null;
  }
  let t17;
  if ($[27] !== claimingMarket || $[28] !== rows) {
    let t18;
    if ($[30] !== claimingMarket) {
      t18 = row => ({
        marketPubkey: row.marketPubkey,
        marketTitle: row.marketTitle ?? null,
        marketImageUrl: row.marketImageUrl ?? null,
        marketState: row.marketState,
        backedAmount: d6ToNumber(row.backedD6),
        vaultSharePct: shareFraction(row.backedD6, row.backedTotalD6),
        earnedAmount: d6ToNumber(row.earnedD6),
        lifetimeEarnedAmount: d6BigToNumber(toBigInt(row.earnedD6) + toBigInt(row.claimedTotalD6)),
        action: rowAction(row),
        isClaiming: claimingMarket === row.marketPubkey
      });
      $[30] = claimingMarket;
      $[31] = t18;
    } else {
      t18 = $[31];
    }
    t17 = orderUnclaimedFirst(rows).map(t18);
    $[27] = claimingMarket;
    $[28] = rows;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  const viewRows = t17;
  let t18;
  if ($[32] !== ctx.userId || $[33] !== ctx.viewerMode || $[34] !== displayName || $[35] !== handle || $[36] !== totals.marketsBacked || $[37] !== totals.totalEarnedD6) {
    t18 = <BackerRewardsShareButton userId={ctx.userId} viewerMode={ctx.viewerMode} handle={handle} displayName={displayName} totalEarnedD6={totals.totalEarnedD6} marketsBacked={totals.marketsBacked} />;
    $[32] = ctx.userId;
    $[33] = ctx.viewerMode;
    $[34] = displayName;
    $[35] = handle;
    $[36] = totals.marketsBacked;
    $[37] = totals.totalEarnedD6;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== totals.totalEarnedD6) {
    t19 = d6ToNumber(totals.totalEarnedD6);
    $[39] = totals.totalEarnedD6;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== totals.claimableNowD6) {
    t20 = d6ToNumber(totals.claimableNowD6);
    $[41] = totals.claimableNowD6;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  const t21 = query.hasNextPage === true;
  let t22;
  if ($[43] !== className || $[44] !== fireClaim || $[45] !== own || $[46] !== query.fetchNextPage || $[47] !== query.isFetchingNextPage || $[48] !== query.itemObserver || $[49] !== t18 || $[50] !== t19 || $[51] !== t20 || $[52] !== t21 || $[53] !== totals.marketsBacked || $[54] !== viewRows) {
    t22 = <BackerRewardsCardView action={t18} totalEarned={t19} claimableNow={t20} marketsBacked={totals.marketsBacked} rows={viewRows} onClaim={fireClaim} canClaimHere={own} getRowRef={query.itemObserver} hasNextPage={t21} isFetchingNextPage={query.isFetchingNextPage} fetchNextPage={query.fetchNextPage} className={className} />;
    $[43] = className;
    $[44] = fireClaim;
    $[45] = own;
    $[46] = query.fetchNextPage;
    $[47] = query.isFetchingNextPage;
    $[48] = query.itemObserver;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
    $[52] = t21;
    $[53] = totals.marketsBacked;
    $[54] = viewRows;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  return t22;
}
const ACTION_ORDER: Readonly<Record<BackerRewardsRowView['action'], number>> = {
  claim: 0,
  accruing: 1,
  claimed: 2
};

/** Unclaimed rewards lead the list; the sort is stable, so each group keeps API order. */
function orderUnclaimedFirst(rows: readonly GetBackerRewardsRow[]): GetBackerRewardsRow[] {
  return [...rows].sort((a, b) => ACTION_ORDER[rowAction(a)] - ACTION_ORDER[rowAction(b)]);
}
function rowAction(row: GetBackerRewardsRow): BackerRewardsRowView['action'] {
  if (row.canClaim && toBigInt(row.earnedD6) > ZERO) return 'claim';
  if (toBigInt(row.claimedTotalD6) > ZERO) return 'claimed';
  return 'accruing';
}
function d6ToNumber(value: string): number {
  return Number(toBigInt(value)) / D6;
}
function d6BigToNumber(value: bigint): number {
  return Number(value) / D6;
}
function shareFraction(backedD6: string, backedTotalD6: string): number {
  const total = toBigInt(backedTotalD6);
  if (total === ZERO) return 0;
  // Scale by D6 before the integer divide so the fraction keeps 6 digits of precision.
  return Number(toBigInt(backedD6) * BigInt(D6) / total) / D6;
}
function toBigInt(value: string): bigint {
  try {
    return BigInt(value);
  } catch {
    // SILENT-CATCH: a non-numeric money string from the wire is treated as zero
    // rather than crashing the whole card; not actionable, so no error reporting.
    return ZERO;
  }
}